import {
  AddressBook,
  CalendarDots,
  House,
  MapTrifold,
  PaperPlaneTilt,
  UserCircle,
} from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * Les cinq onglets, dans leur ordre d'affichage.
 *
 * Cet ordre n'est plus decoratif depuis qu'on balaye d'un onglet a
 * l'autre : c'est lui qui dit ce qu'est « le suivant ». Il vivait en deux
 * exemplaires — la barre et la liste des routes a barre visible — dans
 * deux ordres differents, ce qui aurait envoye le doigt a gauche vers un
 * onglet situe a droite.
 */
export const TABS = [
  { to: '/app/messages', label: t.composants.onglets.messages, Icon: PaperPlaneTilt },
  { to: '/app/events', label: t.composants.onglets.evenements, Icon: CalendarDots },
  { to: '/app/feed', label: t.composants.onglets.feed, Icon: House },
  { to: '/app/lieux', label: t.composants.onglets.lieux, Icon: MapTrifold },
  { to: '/app/profile', label: t.composants.onglets.profil, Icon: UserCircle },
] as const;

/**
 * L'annuaire, a la place des lieux sur un serveur qui en a un.
 *
 * Il ne s'ajoute pas : cinq onglets, c'est ce que la barre tient, et un
 * serveur a annuaire n'a de toute facon pas de trophees (voir
 * `GeoServer.annuaire`).
 */
export const ANNUAIRE = {
  to: '/app/annuaire',
  label: t.composants.onglets.annuaire,
  Icon: AddressBook,
} as const;

/** Les chemins seuls : ceux qui affichent la barre d'onglets. */
export const TAB_PATHS: readonly string[] = TABS.map((tab) => tab.to);

/** Le rang d'un chemin dans la barre, ou `-1` si ce n'est pas un onglet. */
export function tabIndexOf(pathname: string): number {
  return TAB_PATHS.indexOf(pathname);
}

export type Onglet = (typeof TABS)[number] | typeof ANNUAIRE;

/**
 * Les onglets du serveur ou l'on est.
 *
 * Tous, sauf sur un serveur sans trophees : la, l'onglet des medailles
 * n'aurait rien a montrer, et il disparait — ou laisse sa place a
 * l'annuaire, si le serveur en a un. La barre, le balayage d'un onglet a
 * l'autre et les ecrans voisins lisent tous cette liste — c'est elle qui
 * dit ce qu'est « le suivant », il n'en faut qu'une.
 */
export function useOnglets(): { onglets: readonly Onglet[]; chemins: readonly string[] } {
  const { server } = useSession();
  const sans = Boolean(server?.sansTrophees);
  const annuaire = sans && Boolean(server?.annuaire);
  return useMemo(() => {
    const onglets: readonly Onglet[] = !sans
      ? TABS
      : TABS.flatMap((onglet): Onglet[] =>
          onglet.to !== '/app/lieux' ? [onglet] : annuaire ? [ANNUAIRE] : [],
        );
    return { onglets, chemins: onglets.map((onglet) => onglet.to) };
  }, [sans, annuaire]);
}
